import { useAddLookAndFeel } from "@/api/chat-settings/add-look-and-feel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useGetWidgetSettings } from "@/api/chat-settings/get-widget-settings";
import { useUpdateLookAndFeel } from "@/api/chat-settings/update-look-and-feel";
import useGlobalPermission from "@/store/user-permissions";

type Props = {
    settingsData: SettingsDataType;
    setSettingsData: React.Dispatch<React.SetStateAction<SettingsDataType>>;
};

type SettingsDataType = {
    name: string;
    avatar: string;
    welcomeMessage: string;
    widgetColor: string;
    buttonSize: string;
    buttonPosition: string;
};

const defaultValues = {
    name: "Shoe Einstein",
    avatar: `${window.location.origin}/assets/images/logo/bot-icon.png`,
    welcomeMessage: "Hi, I am Shoe Einstein. An AI-based chat assistant to help you with your query. I am available 24/7 for your assistance.",
    widgetColor: "#96cef3",
    buttonSize: "m",
    buttonPosition: "bottom-right",
};

const ChatConfigurationForm: React.FC<Props> = ({ setSettingsData, settingsData }) => {
    const { data: widgetSettings } = useGetWidgetSettings();
    const { mutate: addLookAndFeel } = useAddLookAndFeel();
    const { mutate: updateLookAndFeel } = useUpdateLookAndFeel();
    const onSubmit = () => {
        // console.log({ settingsData });
        if (widgetSettings?.data?.look_and_feel !== null) {
            updateLookAndFeel({
                avatar: settingsData.avatar || defaultValues.avatar,
                name: settingsData.name || defaultValues.name,
                welcome_message: settingsData.welcomeMessage || defaultValues.welcomeMessage,
                widget_button_position: settingsData.buttonPosition || defaultValues.buttonPosition,
                widget_button_size: settingsData.buttonSize || defaultValues.buttonSize,
                widget_color: settingsData.widgetColor || defaultValues.widgetColor,
            });
        } else {
            addLookAndFeel({
                avatar: settingsData.avatar || defaultValues.avatar,
                name: settingsData.name || defaultValues.name,
                welcome_message: settingsData.welcomeMessage || defaultValues.welcomeMessage,
                widget_button_position: settingsData.buttonPosition || defaultValues.buttonPosition,
                widget_button_size: settingsData.buttonSize || defaultValues.buttonSize,
                widget_color: settingsData.widgetColor || defaultValues.widgetColor,
            });
        }
    };

    const valueSetter = (key: string, value: string) => {
        setSettingsData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const { permissionList } = useGlobalPermission();
    const settingsPermission = permissionList.find((item) => item.module.name === "Chat_Settings");
    return (
        <>
            <div>
                <Card className="mb-4 p-4 text-gray-600 shadow-none">
                    <h3 className="text-lg font-bold">Chat Look & Feel</h3>
                    <Separator orientation="horizontal" className="my-4" />
                    <Label htmlFor="name" className="text-lg font-semibold">
                        Name of AI Agent
                    </Label>
                    <Input
                        disabled={!settingsPermission?.can_create}
                        value={settingsData.name}
                        onChange={(e) => valueSetter("name", e.target.value)}
                        id="name"
                        className="mb-4 mt-2 border-2 border-gray-200 py-6"
                        placeholder="Enter the name of AI Agent"
                    />

                    <Label htmlFor="avatar" className="text-lg font-semibold">
                        Avatar of AI Agent
                    </Label>
                    {/* <Input
              {...register('avatar')}
              id="avatar"
              className="mb-4 mt-2 min-h-12 border-2 border-gray-200  pt-3"
              placeholder="Upload the avatar of AI Agent"
              type="file"
            /> */}
                    <Input
                        disabled={!settingsPermission?.can_create}
                        value={settingsData.avatar}
                        onChange={(e) => valueSetter("avatar", e.target.value)}
                        id="avatar"
                        className="mb-4 mt-2 min-h-12 border-2 border-gray-200"
                        placeholder="AI Agent Avatar Link"
                        type="text"
                    />

                    <Label htmlFor="welcome_message" className="text-lg font-semibold">
                        Welcome Message
                    </Label>
                    <Textarea
                        disabled={!settingsPermission?.can_create}
                        value={settingsData.welcomeMessage}
                        onChange={(e) => valueSetter("welcomeMessage", e.target.value)}
                        id="welcome_message"
                        className="mb-4 mt-2 border-2 border-gray-200 py-6"
                        placeholder="Enter the welcome message"
                        rows={3}
                    />
                    <Label htmlFor="widget_color" className="text-lg font-semibold">
                        AI Chatbot Widget Color
                    </Label>
                    <Input
                        disabled={!settingsPermission?.can_create}
                        value={settingsData.widgetColor}
                        onChange={(e) => valueSetter("widgetColor", e.target.value)}
                        defaultValue={"#4990D8"}
                        id="widget_color"
                        className="mb-4 mt-2 min-h-12 border-2 border-gray-200"
                        placeholder="Enter the color of AI Chatbot Widget"
                        type="color"
                    />
                    <Label htmlFor="widget_button_size" className="mb-2 mt-4 block text-lg font-semibold">
                        Chat Button Size
                    </Label>
                    <Select
                        disabled={!settingsPermission?.can_create}
                        value={settingsData.buttonSize}
                        onValueChange={(value) => valueSetter("buttonSize", value)}
                    >
                        <SelectTrigger className="min-h-12 w-full" id="widget_button_size">
                            <SelectValue placeholder="Select a size" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Size</SelectLabel>
                                <SelectItem value="xl">Xtra Large</SelectItem>
                                <SelectItem value="l">Large</SelectItem>
                                <SelectItem value="m">Medium</SelectItem>
                                <SelectItem value="s">Small</SelectItem>
                                <SelectItem value="xs">Xtra Small</SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>

                    <Label htmlFor="widget_button_position" className="mb-2 mt-4 block text-lg font-semibold">
                        Chat Button Position
                    </Label>
                    <Select
                        disabled={!settingsPermission?.can_create}
                        value={settingsData.buttonPosition}
                        onValueChange={(value) => valueSetter("buttonPosition", value)}
                    >
                        <SelectTrigger className="min-h-12 w-full" id="widget_button_position">
                            <SelectValue placeholder="Select a position" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Position</SelectLabel>
                                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                                <SelectItem value="bottom-left">Bottom Left</SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    <Button disabled={!settingsPermission?.can_create} onClick={onSubmit} type="submit" className="mt-4 w-full">
                        Save
                    </Button>
                </Card>
            </div>
        </>
    );
};

export default ChatConfigurationForm;
